/**
 * ChapterGraphView — Cross-Chapter Object Threading Visualization
 *
 * Displays the result of cross-chapter noema analysis:
 * - Summary bar: total chapters, unique objects, through-lines, avg drift
 * - Object threads: table with presence dots, salience, through-line badge
 * - Arc sparklines: tension + novelty per chapter
 * - Transitions: per pair continuing/fresh/dropped, drift %
 *
 * @module @humanizer/studio/components/tools/ChapterGraphView
 */

import React, { useState } from 'react';
import type {
  ChapterGraphResult,
  ChapterGraphObjectThread,
  ChapterGraphTransition,
  ChapterGraphMetric,
  NoeticActCategory,
} from '../../types/analysis';
import '../../styles/chapter-graph.css';

// -----------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------

const CATEGORY_LABELS: Record<NoeticActCategory, string> = {
  constitutive: 'Const.',
  temporal: 'Temporal',
  evaluative: 'Eval.',
  epistemic: 'Epist.',
  relational: 'Relat.',
};

// -----------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------

function SummaryBar({ summary, processingTimeMs }: {
  summary: ChapterGraphResult['summary'];
  processingTimeMs: number;
}) {
  return (
    <div className="analyze-tool__detection">
      <div className="sentencing-v2__summary">
        <div className="sentencing-v2__stat">
          <span className="sentencing-v2__stat-label">Chapters</span>
          <span className="sentencing-v2__stat-value">{summary.totalChapters}</span>
        </div>
        <div className="sentencing-v2__stat">
          <span className="sentencing-v2__stat-label">Objects</span>
          <span className="sentencing-v2__stat-value">{summary.totalUniqueObjects}</span>
        </div>
        <div className="sentencing-v2__stat">
          <span className="sentencing-v2__stat-label">Through-lines</span>
          <span className="sentencing-v2__stat-value">{summary.throughLineCount}</span>
        </div>
        <div className="sentencing-v2__stat">
          <span className="sentencing-v2__stat-label">Avg Drift</span>
          <span className="sentencing-v2__stat-value">{(summary.avgChapterDrift * 100).toFixed(1)}%</span>
        </div>
      </div>
      <div className="analyze-tool__meta">
        {processingTimeMs}ms | chapter-graph | {summary.totalChapters} chapters
      </div>
    </div>
  );
}

function PresenceDots({ thread, totalChapters }: {
  thread: ChapterGraphObjectThread;
  totalChapters: number;
}) {
  const dots: React.ReactElement[] = [];
  for (let i = 0; i < totalChapters; i++) {
    const present = thread.presentIn.includes(i);
    const absent = thread.absentFrom.includes(i);
    dots.push(
      <span
        key={i}
        className={`chapter-graph__dot ${present ? 'chapter-graph__dot--present' : absent ? 'chapter-graph__dot--absent' : 'chapter-graph__dot--none'}`}
        title={`Ch ${i + 1}: ${present ? 'present' : absent ? 'absent (gap)' : 'not yet introduced'}`}
      />
    );
  }
  return <span className="chapter-graph__dots">{dots}</span>;
}

function StanceTrajectory({ thread }: { thread: ChapterGraphObjectThread }) {
  if (thread.stanceTrajectory.length === 0) return null;
  return (
    <div className="chapter-graph__stance-trajectory">
      {thread.stanceTrajectory.map((st, i) => (
        <span key={i} className="chapter-graph__stance-chip" title={`Ch ${st.chapterIndex + 1}`}>
          {st.stance}
        </span>
      ))}
    </div>
  );
}

function ObjectThreadsTable({ threads, totalChapters }: {
  threads: ChapterGraphObjectThread[];
  totalChapters: number;
}) {
  const [expandedLabel, setExpandedLabel] = useState<string | null>(null);

  if (threads.length === 0) {
    return <div className="sentencing-v2__gap">No object threads found.</div>;
  }

  return (
    <div className="chapter-graph__threads">
      <div className="chapter-graph__thread-header">
        <span className="chapter-graph__thread-col chapter-graph__thread-col--label">Object</span>
        <span className="chapter-graph__thread-col chapter-graph__thread-col--type">Type</span>
        <span className="chapter-graph__thread-col chapter-graph__thread-col--presence">Presence</span>
        <span className="chapter-graph__thread-col chapter-graph__thread-col--salience">Sal.</span>
      </div>
      {threads.map(thread => (
        <React.Fragment key={thread.label}>
          <div
            className={`chapter-graph__thread-row ${thread.isThroughLine ? 'chapter-graph__thread-row--throughline' : ''}`}
            onClick={() => setExpandedLabel(expandedLabel === thread.label ? null : thread.label)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setExpandedLabel(expandedLabel === thread.label ? null : thread.label);
              }
            }}
            aria-expanded={expandedLabel === thread.label}
          >
            <span className="chapter-graph__thread-col chapter-graph__thread-col--label">
              {thread.label}
              {thread.isThroughLine && (
                <span className="chapter-graph__throughline-badge" title="Present in >50% of chapters">TL</span>
              )}
            </span>
            <span className="chapter-graph__thread-col chapter-graph__thread-col--type">{thread.objectType}</span>
            <span className="chapter-graph__thread-col chapter-graph__thread-col--presence">
              <PresenceDots thread={thread} totalChapters={totalChapters} />
            </span>
            <span className="chapter-graph__thread-col chapter-graph__thread-col--salience">
              {thread.avgSalience.toFixed(2)}
            </span>
          </div>
          {expandedLabel === thread.label && (
            <div className="chapter-graph__thread-detail">
              <div className="sentencing-v2__detail-row">
                <span className="sentencing-v2__detail-label">Introduced</span>
                <span className="sentencing-v2__detail-value">Ch {thread.introducedChapter + 1}</span>
              </div>
              <div className="sentencing-v2__detail-row">
                <span className="sentencing-v2__detail-label">Last seen</span>
                <span className="sentencing-v2__detail-value">Ch {thread.lastSeenChapter + 1}</span>
              </div>
              {thread.absentFrom.length > 0 && (
                <div className="sentencing-v2__detail-row">
                  <span className="sentencing-v2__detail-label">Gaps</span>
                  <span className="sentencing-v2__detail-value">
                    {thread.absentFrom.map(i => `Ch ${i + 1}`).join(', ')}
                  </span>
                </div>
              )}
              <StanceTrajectory thread={thread} />
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function ArcSparkline({ values, label, colorClass }: {
  values: number[];
  label: string;
  colorClass: string;
}) {
  if (values.length === 0) return null;
  const max = Math.max(...values, 0.01);

  return (
    <div className="chapter-graph__arc">
      <span className="chapter-graph__arc-label">{label}</span>
      <div className="chapter-graph__arc-bars">
        {values.map((v, i) => (
          <div
            key={i}
            className={`chapter-graph__arc-bar ${colorClass}`}
            style={{ height: `${Math.max(2, (v / max) * 100)}%` }}
            title={`Ch ${i + 1}: ${v.toFixed(3)}`}
          />
        ))}
      </div>
    </div>
  );
}

function TransitionsView({ transitions }: { transitions: ChapterGraphTransition[] }) {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  if (transitions.length === 0) {
    return <div className="sentencing-v2__gap">No transitions (need 2+ chapters).</div>;
  }

  return (
    <div className="chapter-graph__transitions">
      {transitions.map((t, i) => (
        <div key={i} className="chapter-graph__transition">
          <div
            className="chapter-graph__transition-header"
            onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setExpandedIdx(expandedIdx === i ? null : i);
              }
            }}
            aria-expanded={expandedIdx === i}
          >
            <span className="chapter-graph__transition-label">
              Ch {t.fromChapter + 1} → Ch {t.toChapter + 1}
            </span>
            <span className="chapter-graph__transition-stats">
              <span className="chapter-graph__transition-stat" title="Continuing">
                ↕ {t.continuingObjects.length}
              </span>
              <span className="chapter-graph__transition-stat chapter-graph__transition-stat--fresh" title="Fresh">
                + {t.freshObjects.length}
              </span>
              <span className="chapter-graph__transition-stat chapter-graph__transition-stat--dropped" title="Dropped">
                − {t.droppedObjects.length}
              </span>
              <span className="chapter-graph__transition-drift">
                {(t.drift * 100).toFixed(1)}%
              </span>
            </span>
          </div>
          {expandedIdx === i && (
            <div className="chapter-graph__transition-detail">
              {t.continuingObjects.length > 0 && (
                <div className="chapter-graph__object-list">
                  <span className="chapter-graph__object-list-label">Continuing:</span>
                  {t.continuingObjects.map(o => (
                    <span key={o} className="outline-panel__noema-chip">{o}</span>
                  ))}
                </div>
              )}
              {t.freshObjects.length > 0 && (
                <div className="chapter-graph__object-list">
                  <span className="chapter-graph__object-list-label chapter-graph__object-list-label--fresh">Fresh:</span>
                  {t.freshObjects.map(o => (
                    <span key={o} className="outline-panel__noema-chip">{o}</span>
                  ))}
                </div>
              )}
              {t.droppedObjects.length > 0 && (
                <div className="chapter-graph__object-list">
                  <span className="chapter-graph__object-list-label chapter-graph__object-list-label--dropped">Dropped:</span>
                  {t.droppedObjects.map(o => (
                    <span key={o} className="outline-panel__noema-chip">{o}</span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ChapterMetricsView({ metrics }: { metrics: ChapterGraphMetric[] }) {
  if (metrics.length === 0) return null;

  return (
    <div className="chapter-graph__metrics-table">
      {metrics.map(m => (
        <div key={m.index} className="chapter-graph__metric-row">
          <span className="chapter-graph__metric-title">
            Ch {m.index + 1}{m.title ? `: ${m.title}` : ''}
          </span>
          <span className="chapter-graph__metric-values">
            {m.objectCount} obj | {m.actCount} acts | {CATEGORY_LABELS[m.dominantCategory]}
          </span>
        </div>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------
// Main Component
// -----------------------------------------------------------------------

export interface ChapterGraphViewProps {
  data: ChapterGraphResult;
}

export function ChapterGraphView({ data }: ChapterGraphViewProps): React.ReactElement {
  const [showSection, setShowSection] = useState<'threads' | 'arcs' | 'transitions' | 'metrics'>('threads');

  return (
    <div className="chapter-graph">
      <SummaryBar summary={data.summary} processingTimeMs={data.processingTimeMs} />

      {/* Section tabs */}
      <div className="analyze-tool__options-grid chapter-graph__tabs" role="tablist">
        {(['threads', 'arcs', 'transitions', 'metrics'] as const).map(tab => (
          <button
            key={tab}
            className={`analyze-option ${showSection === tab ? 'analyze-option--selected' : ''}`}
            onClick={() => setShowSection(tab)}
            role="tab"
            aria-selected={showSection === tab}
          >
            <span className="analyze-option__label">
              {tab === 'threads' ? `Threads (${data.threads.length})`
                : tab === 'arcs' ? 'Arcs'
                : tab === 'transitions' ? `Trans. (${data.transitions.length})`
                : `Metrics (${data.chapterMetrics.length})`}
            </span>
          </button>
        ))}
      </div>

      {/* Tab content */}
      {showSection === 'threads' && (
        <ObjectThreadsTable threads={data.threads} totalChapters={data.summary.totalChapters} />
      )}

      {showSection === 'arcs' && (
        <div className="chapter-graph__arcs">
          <ArcSparkline
            values={data.summary.tensionArc}
            label="Tension"
            colorClass="chapter-graph__arc-bar--tension"
          />
          <ArcSparkline
            values={data.summary.noveltyArc}
            label="Novelty"
            colorClass="chapter-graph__arc-bar--novelty"
          />
        </div>
      )}

      {showSection === 'transitions' && (
        <TransitionsView transitions={data.transitions} />
      )}

      {showSection === 'metrics' && (
        <ChapterMetricsView metrics={data.chapterMetrics} />
      )}
    </div>
  );
}

export default ChapterGraphView;
